import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { WhatsAppPricingService, MessagingLimitConfig } from '../../core/services/whatsapp-pricing.service';
import { WhatsAppAccountService } from '../../core/services/whatsapp-account.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { ONBOARDING_STEPS } from '../../core/config/onboarding-steps';

@Component({
    selector: 'app-messaging-setup',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './messaging-setup.component.html',
    styleUrls: ['./messaging-setup.component.scss']
})
export class MessagingSetupComponent implements OnInit {

    organizationId: string = '';
    isLoading: boolean = false;
    isSaving: boolean = false;
    whatsappAccount: any = null;

    // Step progress tracking
    currentStep: number = 4;
    totalSteps: number = ONBOARDING_STEPS.length;
    progressPercentage: number = 0;

    limits: MessagingLimitConfig = {
        marketing: { limit: null, action: 'none' },
        utility: { limit: null, action: 'none' },
        authentication: { limit: null, action: 'none' },
        service: { limit: null, action: 'none' }
    };

    categories: { key: keyof MessagingLimitConfig; label: string; description: string; icon: string }[] = [
        { key: 'marketing', label: 'Marketing', description: 'Promotional messages, offers, and announcements', icon: 'bx-megaphone' },
        { key: 'utility', label: 'Utility', description: 'Transaction updates, account alerts, and confirmations', icon: 'bx-bell' },
        { key: 'authentication', label: 'Authentication', description: 'One-time passcodes and login verification codes', icon: 'bx-lock-alt' },
        // Chargeable from 1 October 2026, when Meta stops giving away free-form
        // replies inside the 24-hour window.
        { key: 'service', label: 'Support replies', description: 'Free-form replies your team sends inside the 24-hour window', icon: 'bx-support' }
    ];

    private auth = inject(Auth);
    private authState$ = authState(this.auth);
    private pricingService = inject(WhatsAppPricingService);
    private whatsappService = inject(WhatsAppAccountService);
    private onboardingService = inject(OnboardingService);

    constructor(private router: Router) { }

    async ngOnInit(): Promise<void> {
        const currentUser = await firstValueFrom(this.authState$);
        if (currentUser) {
            // Membership comes from the user document, not the Planning Center
            // sign-in claim: a standalone organisation has no such claim.
            const setup = await this.onboardingService.getSetupContext(
                currentUser.uid,
                'messaging-setup'
            );
            this.organizationId = setup.organizationId;
            this.currentStep = setup.currentStep;
            this.totalSteps = setup.totalSteps;
            this.progressPercentage = setup.progressPercentage;
        }


        if (this.organizationId) {
            this.loadExistingLimits();
            this.loadWhatsAppAccount();
        }
    }

    loadExistingLimits() {
        this.isLoading = true;
        this.pricingService.getMessagingLimits(this.organizationId).subscribe({
            next: (existing) => {
                if (existing) {
                    this.limits = {
                        marketing: { ...existing.marketing },
                        utility: { ...existing.utility },
                        authentication: { ...existing.authentication },
                        service: { ...existing.service }
                    };
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading messaging limits:', error);
                this.isLoading = false;
            }
        });
    }

    loadWhatsAppAccount() {
        this.whatsappService.getWhatsAppAccount(this.organizationId).subscribe({
            next: (account) => {
                this.whatsappAccount = account;
            },
            error: (error) => {
                console.error('Error loading WhatsApp account:', error);
            }
        });
    }

    openMetaPaymentSettings() {
        const wabaId = this.whatsappAccount?.wabaIds?.[0];
        const businessId = this.whatsappAccount?.businessId;

        let paymentUrl = 'https://business.facebook.com/latest/settings/whatsapp_account/';

        if (businessId && wabaId) {
            paymentUrl += `?business_id=${businessId}&selected_asset_id=${wabaId}&selected_asset_type=whatsapp-business-account`;
        } else if (businessId) {
            paymentUrl += `?business_id=${businessId}`;
        }

        window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    }

    async saveAndContinue() {
        this.isSaving = true;

        Swal.fire({
            title: 'Saving messaging limits...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        this.pricingService.saveMessagingLimits(this.organizationId, this.limits).subscribe({
            next: async () => {
                this.isSaving = false;
                await this.onboardingService.completeStep(this.organizationId, 'messaging-setup');
                const nextStep = await this.onboardingService.resolveNextStep(this.organizationId);
                Swal.fire({
                    icon: 'success',
                    title: 'Messaging Limits Saved',
                    text: 'Your messaging limits have been configured. You can update these anytime from Settings.',
                    confirmButtonColor: '#556ee6',
                    timer: 3000,
                    timerProgressBar: true
                }).then(() => {
                    this.router.navigate([nextStep ? nextStep.route : '/']);
                });
            },
            error: (error) => {
                this.isSaving = false;
                console.error('Error saving messaging limits:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Save Failed',
                    text: error.message || 'Failed to save messaging limits. Please try again.',
                    confirmButtonColor: '#556ee6'
                });
            }
        });
    }

    async skip() {
        await this.onboardingService.completeStep(this.organizationId, 'messaging-setup', 'skipped');
        const nextStep = await this.onboardingService.resolveNextStep(this.organizationId);
        this.router.navigate([nextStep ? nextStep.route : '/']);
    }
}
